import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    buildOsmStaticMapUrl,
    DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL,
    type ICampaignThreadLocationPoint,
} from '../../../utils/campaignThreadLocation';

export type CampaignThreadLocationVariant = 'brand' | 'creator';

const CREATOR_GOLD = '#ffc965';
const CREATOR_ON_GOLD = '#392700';

export function CampaignThreadLocationCard({
    location,
    variant,
    isMine,
}: {
    location: ICampaignThreadLocationPoint;
    variant: CampaignThreadLocationVariant;
    isMine: boolean;
}) {
    const isCreator = variant === 'creator';
    const accent = isCreator ? (isMine ? CREATOR_ON_GOLD : CREATOR_GOLD) : '#ffc965';
    const label = location.label?.trim() || DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL;

    return (
        <Box
            sx={{
                mt: 1.5,
                width: '100%',
                maxWidth: 280,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                bgcolor: 'rgba(0,0,0,0.2)',
            }}>
            <Box
                component="a"
                href={location.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    display: 'block',
                    position: 'relative',
                    height: 120,
                    textDecoration: 'none',
                }}>
                <Box
                    component="img"
                    src={buildOsmStaticMapUrl(location.lat, location.lng)}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.15)',
                    }}>
                    <LocationOnOutlinedIcon sx={{ fontSize: 36, color: accent }} />
                </Box>
            </Box>
            <Box sx={{ px: 1.5, py: 1.25 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: accent, mb: 0.25 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', mb: 1 }}>
                    {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
                </Typography>
                <Button
                    component="a"
                    href={location.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    fullWidth
                    endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
                    sx={{
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 12,
                        bgcolor: isCreator
                            ? isMine
                                ? 'rgba(57,39,0,0.12)'
                                : 'rgba(255,201,101,0.12)'
                            : 'rgba(255,201,101,0.12)',
                        color: accent,
                        '&:hover': {
                            bgcolor: isCreator
                                ? isMine
                                    ? 'rgba(57,39,0,0.2)'
                                    : 'rgba(255,201,101,0.2)'
                                : 'rgba(255,201,101,0.2)',
                        },
                    }}>
                    Open in Maps
                </Button>
            </Box>
        </Box>
    );
}
